import { useEffect } from 'react';

import Modal from '@/components/common/Modal';
import Spacer from '@/components/common/Spacer';
import Text from '@/components/common/Text';
import Button from '@/components/common/Button';
import useMonthlyBillDetail from '@/hooks/useMonthlyBillDetail';
import type { MonthlyBillDetail } from '@/types/company.types';
import { dateFormat } from '@/utils/convertDateFormat';
import { Theme } from '@/styles/Theme';
import { getMonthlyBillStatusLabel, getMonthlyBillStatusColor } from '@/utils/monthlyBillStatus';

import * as styled from './style';

interface MonthlyBillDetailModalProps {
  billId: number | null;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onPayClick?: (bill: MonthlyBillDetail) => void;
}

const getPaymentMethodLabel = (method: MonthlyBillDetail['payment_method']) => {
  return method === 'prepaid' ? '선불' : '후불';
};

const MonthlyBillDetailModal = ({
  billId,
  setIsModalOpen,
  onPayClick,
}: MonthlyBillDetailModalProps) => {
  const { data: billDetailData, isLoading } = useMonthlyBillDetail(billId);

  const bill = billDetailData?.data?.bill;

  const handlePayClick = () => {
    if (bill && onPayClick) {
      onPayClick(bill);
    }
  };

  const canPay = bill && (bill.status === 'sent' || bill.status === 'failed');

  if (!billId) {
    return null;
  }

  return (
    <Modal setIsModalOpen={setIsModalOpen}>
      <styled.ModalContent>
        {isLoading ? (
          <Text fontSize='1rem' color={Theme.colors.G_1}>
            로딩 중...
          </Text>
        ) : !bill ? (
          <Text fontSize='1rem' color={Theme.colors.WARNING_RED}>
            청구서를 찾을 수 없습니다.
          </Text>
        ) : (
          <>
            <styled.Header>
              <Text fontSize='1.5rem' fontWeight={700}>
                청구서 상세
              </Text>
              <styled.StatusBadge color={getMonthlyBillStatusColor(bill.status)}>
                {getMonthlyBillStatusLabel(bill.status)}
              </styled.StatusBadge>
            </styled.Header>

            <Spacer y='1.5rem' />

            <styled.InfoSection>
              <styled.InfoRow>
                <styled.InfoLabel>청구 기간:</styled.InfoLabel>
                <styled.InfoValue>
                  {dateFormat(bill.billing_period_start)} ~{' '}
                  {dateFormat(bill.billing_period_end)}
                </styled.InfoValue>
              </styled.InfoRow>
              <styled.InfoRow>
                <styled.InfoLabel>결제 방식:</styled.InfoLabel>
                <styled.InfoValue>{getPaymentMethodLabel(bill.payment_method)}</styled.InfoValue>
              </styled.InfoRow>
              <styled.InfoRow>
                <styled.InfoLabel>작업자 수:</styled.InfoLabel>
                <styled.InfoValue>{bill.worker_count}명</styled.InfoValue>
              </styled.InfoRow>
              <styled.InfoRow>
                <styled.InfoLabel>총 금액:</styled.InfoLabel>
                <styled.InfoValue>
                  {bill.total_amount.toLocaleString('ko-KR')}원
                </styled.InfoValue>
              </styled.InfoRow>
              {bill.sent_at && (
                <styled.InfoRow>
                  <styled.InfoLabel>발송일:</styled.InfoLabel>
                  <styled.InfoValue>{dateFormat(bill.sent_at)}</styled.InfoValue>
                </styled.InfoRow>
              )}
            </styled.InfoSection>

            <Spacer y='1.5rem' />

            <styled.WorkersSection>
              <Text fontSize='1.125rem' fontWeight={600} margin='0 0 0.75rem 0'>
                작업자별 내역
              </Text>
              <styled.WorkersTable>
                <styled.WorkersThead>
                  <styled.WorkersTr>
                    <styled.WorkersTh>작업자 이름</styled.WorkersTh>
                    <styled.WorkersTh>단가</styled.WorkersTh>
                    <styled.WorkersTh>활성 기간</styled.WorkersTh>
                    <styled.WorkersTh>금액</styled.WorkersTh>
                  </styled.WorkersTr>
                </styled.WorkersThead>
                <styled.WorkersTbody>
                  {bill.workers && bill.workers.length > 0 ? (
                    bill.workers.map((worker) => (
                      <styled.WorkersTr key={worker.id}>
                        <styled.WorkersTd>{worker.worker_name || '-'}</styled.WorkersTd>
                        <styled.WorkersTd>
                          {worker.price_per_worker.toLocaleString('ko-KR')}원
                        </styled.WorkersTd>
                        <styled.WorkersTd>
                          {dateFormat(worker.active_from)}
                          {worker.active_to && ` ~ ${dateFormat(worker.active_to)}`}
                        </styled.WorkersTd>
                        <styled.WorkersTd>
                          {worker.amount.toLocaleString('ko-KR')}원
                        </styled.WorkersTd>
                      </styled.WorkersTr>
                    ))
                  ) : (
                    <styled.WorkersTr>
                      <styled.WorkersTd colSpan={4}>
                        <Text fontSize='0.875rem' color={Theme.colors.G_1}>
                          작업자 정보가 없습니다.
                        </Text>
                      </styled.WorkersTd>
                    </styled.WorkersTr>
                  )}
                </styled.WorkersTbody>
              </styled.WorkersTable>
            </styled.WorkersSection>

            <Spacer y='2rem' />

            <styled.ButtonWrapper>
              {canPay && (
                <Button
                  backgroundColor={Theme.colors.B_3}
                  color={Theme.colors.WHITE}
                  height='2.5rem'
                  width='10rem'
                  onClick={handlePayClick}
                >
                  결제하기
                </Button>
              )}
              <Button
                backgroundColor={Theme.colors.G_5}
                color={Theme.colors.BLACK}
                height='2.5rem'
                width='10rem'
                onClick={() => setIsModalOpen(false)}
              >
                닫기
              </Button>
            </styled.ButtonWrapper>
          </>
        )}
      </styled.ModalContent>
    </Modal>
  );
};

export default MonthlyBillDetailModal;

